import React, { Suspense } from 'react'
import { useFragment, graphql } from 'react-relay'
import { Chip, Avatar, Typography } from '@mui/material'
import { useTheme, lighten } from '@mui/material/styles'
import FocusFadeGroup from 'components/FocusFadeGroup'
import { AddCircle, Create } from '@mui/icons-material'
import { SpecializationsFields_User$key } from '__generated__/SpecializationsFields_User.graphql'
import { useNodes } from 'hooks/relay'
import useStyles from './styles'
import SpecializationFieldsDialog from './components/SpecializationFieldsDialog'
import Specialization from './components/Specialization'

export interface ISpecializationsFieldsProps {
  user: SpecializationsFields_User$key
  editable: boolean
}

function SpecializationsFields(props: ISpecializationsFieldsProps) {
  const { classes } = useStyles()
  const theme = useTheme()
  const { user: userFragmentRef, editable } = props

  const [dialogOpened, setDialogOpened] = React.useState(false)

  const user = useFragment(graphql`
    fragment SpecializationsFields_User on User {
      profile {
        industry {
          id
        }
        specializations {
          id 
          name

          ...Specialization_Specialization
        }
      }

      ...SpecializationFieldsDialog_User
    }
  `, userFragmentRef)

  const specializations = useNodes(user?.profile?.specializations)
  const hasSpecializations = specializations.length > 0
  const showSpecializations = Boolean(editable || specializations.length)

  return (
    (
      <div style={{ marginBottom: theme.spacing(-1) }}>
        <FocusFadeGroup focused={false}>
          {showSpecializations && (
          // TODO: replace this with <Collapse>. I removed it due to an issue with infinite setState recursion
            (
            <div>
              {specializations?.map(specialization => (
                <Specialization key={specialization.id} specialization={specialization} />
              ))}
              {editable && (
              <Chip
                variant="outlined"
                style={{ margin: theme.spacing(0, 1, 1, 0), borderColor: lighten(theme.palette.secondary.main, 0.17), color: lighten(theme.palette.secondary.main, 0.17), cursor: 'pointer' }}
                label={(
                  <Typography variant="body2" className={classes.expertiseContent} style={{ color: lighten(theme.palette.secondary.main, 0.17) }}>
                    {hasSpecializations ? 'Edit' : 'Add your specializations'}
                  </Typography>
                )}
                avatar={(
                  <Avatar style={{ color: lighten(theme.palette.secondary.main, 0.17), background: 'none', height: 32, width: 32 }}>
                    {hasSpecializations ? <Create /> : <AddCircle />}
                  </Avatar>
                )}
                onClick={() => setDialogOpened(true)}
                data-cy="profile-specializations-edit"
              />
              )}
            </div>
            )
          )}

          {dialogOpened && (
            <Suspense>
              <SpecializationFieldsDialog
                user={user}
                onClose={() => setDialogOpened(false)}
              />
            </Suspense>
          )}
        </FocusFadeGroup>
      </div>
    )
  )
}

export default React.memo(SpecializationsFields)
